import {
  serviceDetailPath,
  sortServicesHighlightedFirst,
  type MarketplaceService,
} from './marketplace-services'
import { normalizeForSearch, searchIncludes } from './normalize-search'

export type PredictionService = {
  kind: 'service'
  id: number
  label: string
  href: string
}

export type PredictionLocation = {
  kind: 'location'
  id: number
  label: string
  /** `?location=ID` – applied on homepage */
  href: string
}

export type PredictionCategory = {
  kind: 'category'
  id: number
  label: string
  /** `?category=ID` – applied on homepage */
  href: string
}

export type Prediction = PredictionService | PredictionLocation | PredictionCategory

export type PredictionGroups = {
  services: PredictionService[]
  locations: PredictionLocation[]
  categories: PredictionCategory[]
}

const MAX_SERVICES = 5
const MAX_LOCATIONS = 4
const MAX_CATEGORIES = 4

export function buildPredictions(
  query: string,
  services: MarketplaceService[]
): PredictionGroups {
  const q = normalizeForSearch(query)
  if (q.length < 3) return { services: [], locations: [], categories: [] }

  const ordered = sortServicesHighlightedFirst(services)

  // ── Services by title ──────────────────────────────────────────────────────
  const seenHrefs = new Set<string>()
  const matchedServices: PredictionService[] = []

  for (const s of ordered) {
    if (!searchIncludes(s.title, q)) continue
    const href = serviceDetailPath(s)
    if (!href || seenHrefs.has(href)) continue
    seenHrefs.add(href)
    matchedServices.push({ kind: 'service', id: s.id, label: s.title, href })
    if (matchedServices.length >= MAX_SERVICES) break
  }

  // ── Locations ──────────────────────────────────────────────────────────────
  const seenLocIds = new Set<number>()
  const matchedLocations: PredictionLocation[] = []

  for (const s of ordered) {
    if (matchedLocations.length >= MAX_LOCATIONS) break
    for (const loc of s.locations) {
      if (matchedLocations.length >= MAX_LOCATIONS) break
      if (seenLocIds.has(loc.id)) continue
      const name = (loc.locationName ?? loc.city ?? '').trim()
      if (!name) continue
      if (!searchIncludes(name, q)) continue
      seenLocIds.add(loc.id)
      matchedLocations.push({
        kind: 'location',
        id: loc.id,
        label: name,
        href: `/?location=${loc.id}#usluge`,
      })
    }
  }

  // ── Categories ────────────────────────────────────────────────────────────
  const seenCatIds = new Set<number>()
  const matchedCategories: PredictionCategory[] = []

  for (const s of ordered) {
    if (matchedCategories.length >= MAX_CATEGORIES) break
    for (const cat of s.categories) {
      if (matchedCategories.length >= MAX_CATEGORIES) break
      if (seenCatIds.has(cat.id)) continue
      if (!searchIncludes(cat.name, q)) continue
      seenCatIds.add(cat.id)
      matchedCategories.push({
        kind: 'category',
        id: cat.id,
        label: cat.name,
        href: `/?category=${cat.id}#usluge`,
      })
    }
  }

  return { services: matchedServices, locations: matchedLocations, categories: matchedCategories }
}

export function hasAnyPrediction(groups: PredictionGroups): boolean {
  return (
    groups.services.length > 0 ||
    groups.locations.length > 0 ||
    groups.categories.length > 0
  )
}
